/**
 * NavigatorX Fleet Manager
 *
 * Comprehensive superadmin dashboard for managing fleet company accounts.
 * Reads from nav_companies collection and its subcollections
 * (members, jobs, messages, alerts).
 *
 * Features:
 * - Create company (with owner lookup, invite code generation, state selection)
 * - Company list with billing status filtering, column sorting, search
 * - CSV export of all company data
 * - Company detail view: members, jobs, alerts, messages, settings
 * - Update company settings (seat count, billing, thresholds, states)
 * - Update member roles, suspend/remove members with confirmations
 * - Delete company with full cleanup
 * - Aggregate fleet statistics (utilization %, MRR)
 * - Empty state with create CTA
 *
 * @version 2.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Progress } from '../../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Building2,
  Users,
  Search,
  RefreshCw,
  Loader2,
  ChevronLeft,
  MapPin,
  Briefcase,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  Clock,
  Copy,
  Eye,
  Edit,
  Shield,
  Truck,
  UserCheck,
  UserX,
  Gauge,
  Timer,
  Save,
  X,
  Plus,
  Download,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Trash2,
  DollarSign,
  BarChart3,
  CreditCard,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getFleetCompanies,
  getFleetMembers,
  getFleetJobs,
  getFleetAlerts,
  getFleetMessages,
  getFleetStats,
  getFleetInvoices,
  getFleetQuotes,
  updateFleetCompany,
  updateFleetCompanyBilling,
  updateFleetMember,
  removeFleetMember,
  createFleetCompany,
  deleteFleetCompany,
  exportFleetCompaniesCSV,
  callCreateFleetSubscription,
  callUpdateFleetSubscription,
  callCancelFleetSubscription,
  callGenerateFleetQuote,
  callGenerateFleetInvoice,
  callSendFleetInvoice,
  callSetCustomPricing,
  callGetBillingPortalUrl,
  US_STATES,
  type FleetCompany,
  type FleetMember,
  type FleetJob,
  type FleetAlert,
  type FleetMessage,
  type FleetStats,
  type FleetInvoice,
  type FleetQuote,
  type CreateCompanyInput,
} from '../../../utils/services/navigatorXFleetService';
import {
  FileText,
  Send,
  ExternalLink,
  Receipt,
} from 'lucide-react';
import FleetVoicePolicyPanel from './FleetVoicePolicyPanel';

// ============================================================================
// TYPES
// ============================================================================

interface FleetManagerProps {
  embedded?: boolean;
}

type DetailTab = 'members' | 'jobs' | 'alerts' | 'messages' | 'billing' | 'settings';
type BillingFilter = 'ALL' | 'ACTIVE' | 'TRIALING' | 'PAST_DUE' | 'CANCELLED';
type SortField = 'name' | 'ownerEmail' | 'billingStatus' | 'memberCount' | 'seatCount' | 'createdAt';
type SortDirection = 'asc' | 'desc';

interface ConfirmAction {
  title: string;
  description: string;
  variant: 'danger' | 'warning';
  confirmLabel: string;
  onConfirm: () => Promise<void>;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const BILLING_FILTER_OPTIONS: { value: BillingFilter; label: string }[] = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'TRIALING', label: 'Trialing' },
  { value: 'PAST_DUE', label: 'Past Due' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const DEFAULT_CREATE_FORM: CreateCompanyInput = {
  name: '',
  ownerEmail: '',
  seatCount: 5,
  billingStatus: 'ACTIVE',
  homeState: '',
  activeStates: [],
  idleAlertMinutes: 30,
  speedLimitMph: 75,
};

// ============================================================================
// HELPERS
// ============================================================================

function formatTimestamp(ts: any): string {
  if (!ts) return '—';
  try {
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatShortDate(ts: any): string {
  if (!ts) return '—';
  try {
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

function formatTimeAgo(ts: any): string {
  if (!ts) return 'Never';
  try {
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDays = Math.floor(diffHr / 24);
    return `${diffDays}d ago`;
  } catch {
    return 'Unknown';
  }
}

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function billingBadge(status: string) {
  switch (status) {
    case 'ACTIVE':
      return <Badge className="bg-green-100 text-green-700 border-green-300">Active</Badge>;
    case 'TRIALING':
      return <Badge className="bg-blue-100 text-blue-700 border-blue-300">Trial</Badge>;
    case 'PAST_DUE':
      return <Badge className="bg-red-100 text-red-700 border-red-300">Past Due</Badge>;
    case 'CANCELLED':
      return <Badge className="bg-gray-100 text-gray-700 border-gray-300">Cancelled</Badge>;
    default:
      return <Badge className="bg-gray-100 text-gray-500 border-gray-300">{status}</Badge>;
  }
}

function roleBadge(role: string) {
  switch (role) {
    case 'owner':
      return <Badge className="bg-purple-100 text-purple-700 border-purple-300">Owner</Badge>;
    case 'admin':
      return <Badge className="bg-blue-100 text-blue-700 border-blue-300">Admin</Badge>;
    case 'driver':
      return <Badge className="bg-gray-100 text-gray-700 border-gray-300">Driver</Badge>;
    default:
      return <Badge variant="outline">{role}</Badge>;
  }
}

function statusBadge(status: string) {
  switch (status) {
    case 'active':
      return <Badge className="bg-green-100 text-green-700">Active</Badge>;
    case 'suspended':
      return <Badge className="bg-yellow-100 text-yellow-700">Suspended</Badge>;
    case 'removed':
      return <Badge className="bg-red-100 text-red-700">Removed</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function jobStatusBadge(status: string) {
  switch (status) {
    case 'pending':
      return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
    case 'accepted':
      return <Badge className="bg-blue-100 text-blue-700">Accepted</Badge>;
    case 'in_progress':
      return <Badge className="bg-indigo-100 text-indigo-700">In Progress</Badge>;
    case 'completed':
      return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
    case 'cancelled':
      return <Badge className="bg-red-100 text-red-700">Cancelled</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function downloadCsvFile(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getTimestampSeconds(ts: unknown): number {
  if (!ts || typeof ts !== 'object') return 0;
  const record = ts as Record<string, unknown>;
  if (typeof record.seconds === 'number') return record.seconds;
  if (typeof record.toDate === 'function') {
    try {
      return Math.floor((record.toDate as () => Date)().getTime() / 1000);
    } catch {
      return 0;
    }
  }
  return 0;
}

// ============================================================================
// SORT HEADER (module-level to avoid re-creation on parent re-render)
// ============================================================================

function SortHeader({
  field,
  label,
  align = 'left',
  activeSortField,
  activeSortDirection,
  onSort,
}: {
  field: SortField;
  label: string;
  align?: 'left' | 'center';
  activeSortField: SortField;
  activeSortDirection: SortDirection;
  onSort: (field: SortField) => void;
}) {
  const isActive = activeSortField === field;
  return (
    <th
      className={`${align === 'center' ? 'text-center' : 'text-left'} px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-gray-900 transition-colors`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          activeSortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </span>
    </th>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function FleetManager({ embedded: _embedded = false }: FleetManagerProps) {
  // ── List view state ──
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<FleetCompany[]>([]);
  const [stats, setStats] = useState<FleetStats | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [billingFilter, setBillingFilter] = useState<BillingFilter>('ALL');
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});

  // ── Selected company / detail view ──
  const [selectedCompany, setSelectedCompany] = useState<FleetCompany | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('members');
  const [members, setMembers] = useState<FleetMember[]>([]);
  const [jobs, setJobs] = useState<FleetJob[]>([]);
  const [alerts, setAlerts] = useState<FleetAlert[]>([]);
  const [messages, setMessages] = useState<FleetMessage[]>([]);
  const [invoices, setInvoices] = useState<FleetInvoice[]>([]);
  const [quotes, setQuotes] = useState<FleetQuote[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── Billing modals ──
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [billingActionLoading, setBillingActionLoading] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ contactName: '', contactEmail: '', seatCount: 0, pricePerSeat: 0, notes: '' });
  const [invoiceForm, setInvoiceForm] = useState({ periodStart: '', periodEnd: '', notes: '' });
  const [pricingForm, setPricingForm] = useState({ pricePerSeat: 0, customDiscount: 0, tierName: '' });

  // ── Create company modal ──
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateCompanyInput>({ ...DEFAULT_CREATE_FORM });
  const [creating, setCreating] = useState(false);
  const [createdInviteCode, setCreatedInviteCode] = useState<string | null>(null);

  // ── Edit company modal ──
  const [editingCompany, setEditingCompany] = useState<FleetCompany | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    seatCount: 0,
    billingStatus: '',
    idleAlertMinutes: 30,
    speedLimitMph: 75,
    homeState: '',
    activeStates: [] as string[],
  });
  const [saving, setSaving] = useState(false);

  // ── Confirmation dialog ──
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [confirming, setConfirming] = useState(false);

  // ── CSV export ──
  const [exporting, setExporting] = useState(false);

  // ════════════════════════════════════════════════════════════════════════════
  // DATA LOADING
  // ════════════════════════════════════════════════════════════════════════════

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const [companiesData, statsData] = await Promise.all([
        getFleetCompanies(),
        getFleetStats(),
      ]);
      setCompanies(companiesData);
      setStats(statsData);

      const counts: Record<string, number> = {};
      const memberResults = await Promise.allSettled(
        companiesData.map(async (company) => {
          const membersData = await getFleetMembers(company.id);
          return { id: company.id, count: membersData.filter(m => m.status === 'active').length };
        })
      );
      for (const result of memberResults) {
        if (result.status === 'fulfilled') {
          counts[result.value.id] = result.value.count;
        } else {
          console.error('Failed to load member count for a company:', result.reason);
        }
      }
      setMemberCounts(counts);
    } catch (error) {
      console.error('Error loading fleet companies:', error);
      toast.error('Failed to load fleet companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const loadCompanyDetails = useCallback(async (company: FleetCompany) => {
    setDetailLoading(true);
    try {
      const [membersData, jobsData, alertsData, messagesData, invoicesData, quotesData] = await Promise.all([
        getFleetMembers(company.id),
        getFleetJobs(company.id),
        getFleetAlerts(company.id),
        getFleetMessages(company.id),
        getFleetInvoices(company.id),
        getFleetQuotes(company.id),
      ]);
      setMembers(membersData);
      setJobs(jobsData);
      setAlerts(alertsData);
      setMessages(messagesData);
      setInvoices(invoicesData);
      setQuotes(quotesData);
    } catch (error) {
      console.error('Error loading company details:', error);
      toast.error('Failed to load company details');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleSelectCompany = useCallback((company: FleetCompany) => {
    setSelectedCompany(company);
    setDetailTab('members');
    loadCompanyDetails(company);
  }, [loadCompanyDetails]);

  // ════════════════════════════════════════════════════════════════════════════
  // CREATE COMPANY
  // ════════════════════════════════════════════════════════════════════════════

  const openCreateModal = useCallback(() => {
    setCreateForm({ ...DEFAULT_CREATE_FORM });
    setCreatedInviteCode(null);
    setShowCreateModal(true);
  }, []);

  const handleCreateCompany = useCallback(async () => {
    if (!createForm.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!createForm.ownerEmail.trim()) {
      toast.error('Owner email is required');
      return;
    }

    setCreating(true);
    try {
      const result = await createFleetCompany(createForm);
      setCreatedInviteCode(result.inviteCode);
      toast.success(`Created "${createForm.name}" — Invite code: ${result.inviteCode}`);
      loadCompanies();
    } catch (error: any) {
      console.error('Error creating company:', error);
      toast.error(error.message || 'Failed to create company');
    } finally {
      setCreating(false);
    }
  }, [createForm, loadCompanies]);

  const toggleActiveState = useCallback((stateCode: string, form: 'create' | 'edit') => {
    if (form === 'create') {
      setCreateForm(prev => ({
        ...prev,
        activeStates: prev.activeStates.includes(stateCode)
          ? prev.activeStates.filter(s => s !== stateCode)
          : [...prev.activeStates, stateCode],
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        activeStates: prev.activeStates.includes(stateCode)
          ? prev.activeStates.filter(s => s !== stateCode)
          : [...prev.activeStates, stateCode],
      }));
    }
  }, []);

  // ════════════════════════════════════════════════════════════════════════════
  // EDIT COMPANY
  // ════════════════════════════════════════════════════════════════════════════

  const openEditCompany = useCallback((company: FleetCompany) => {
    setEditingCompany(company);
    setEditForm({
      name: company.name,
      seatCount: company.seatCount,
      billingStatus: company.billingStatus,
      idleAlertMinutes: company.idleAlertMinutes,
      speedLimitMph: company.speedLimitMph,
      homeState: company.homeState,
      activeStates: [...company.activeStates],
    });
  }, []);

  const executeSaveCompany = useCallback(async () => {
    if (!editingCompany) return;
    setSaving(true);
    try {
      await updateFleetCompany(editingCompany.id, {
        name: editForm.name,
        seatCount: editForm.seatCount,
        billingStatus: editForm.billingStatus,
        idleAlertMinutes: editForm.idleAlertMinutes,
        speedLimitMph: editForm.speedLimitMph,
        homeState: editForm.homeState,
        activeStates: editForm.activeStates,
      });
      toast.success(`Updated ${editForm.name}`);
      setEditingCompany(null);
      loadCompanies();
      if (selectedCompany?.id === editingCompany.id) {
        const refreshed = { ...selectedCompany, ...editForm };
        setSelectedCompany(refreshed as FleetCompany);
        loadCompanyDetails(refreshed as FleetCompany);
      }
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error('Failed to update company');
    } finally {
      setSaving(false);
    }
  }, [editingCompany, editForm, loadCompanies, selectedCompany, loadCompanyDetails]);

  const handleSaveCompany = useCallback(async () => {
    if (!editingCompany) return;

    const changingToCancelled =
      editForm.billingStatus === 'CANCELLED' && editingCompany.billingStatus !== 'CANCELLED';

    if (changingToCancelled) {
      setConfirmAction({
        title: 'Cancel Billing',
        description: `Cancel billing for "${editingCompany.name}"? Members will retain access until the current period ends.`,
        variant: 'warning',
        confirmLabel: 'Cancel Billing',
        onConfirm: async () => {
          await executeSaveCompany();
        },
      });
      return;
    }

    await executeSaveCompany();
  }, [editingCompany, editForm, executeSaveCompany]);

  // ════════════════════════════════════════════════════════════════════════════
  // DELETE COMPANY
  // ════════════════════════════════════════════════════════════════════════════

  const handleDeleteCompany = useCallback((company: FleetCompany) => {
    setConfirmAction({
      title: 'Delete Company',
      description: `Permanently delete "${company.name}"? This removes all members, jobs, messages, alerts, and revokes fleet access for every member. This action cannot be undone.`,
      variant: 'danger',
      confirmLabel: 'Delete Forever',
      onConfirm: async () => {
        await deleteFleetCompany(company.id);
        toast.success(`Deleted "${company.name}"`);
        setSelectedCompany(null);
        loadCompanies();
      },
    });
  }, [loadCompanies]);

  // ════════════════════════════════════════════════════════════════════════════
  // MEMBER ACTIONS
  // ════════════════════════════════════════════════════════════════════════════

  const handleUpdateMemberRole = useCallback(async (member: FleetMember, newRole: 'admin' | 'driver') => {
    if (!selectedCompany) return;
    try {
      await updateFleetMember(selectedCompany.id, member.uid, { role: newRole });
      toast.success(`Changed ${member.displayName || member.email} to ${newRole}`);
      loadCompanyDetails(selectedCompany);
    } catch (error) {
      console.error('Error updating member role:', error);
      toast.error('Failed to update member role');
    }
  }, [selectedCompany, loadCompanyDetails]);

  const handleSuspendMember = useCallback(async (member: FleetMember) => {
    if (!selectedCompany) return;
    const newStatus = member.status === 'suspended' ? 'active' : 'suspended';
    try {
      await updateFleetMember(selectedCompany.id, member.uid, { status: newStatus });
      toast.success(`${newStatus === 'suspended' ? 'Suspended' : 'Reactivated'} ${member.displayName || member.email}`);
      loadCompanyDetails(selectedCompany);
    } catch (error) {
      console.error('Error suspending member:', error);
      toast.error('Failed to update member status');
    }
  }, [selectedCompany, loadCompanyDetails]);

  const handleRemoveMember = useCallback((member: FleetMember) => {
    if (!selectedCompany) return;
    const memberName = member.displayName || member.email;
    setConfirmAction({
      title: 'Remove Member',
      description: `Remove ${memberName} from ${selectedCompany.name}? This will revoke their fleet access and clear their company association.`,
      variant: 'warning',
      confirmLabel: 'Remove Member',
      onConfirm: async () => {
        await removeFleetMember(selectedCompany.id, member.uid);
        toast.success(`Removed ${memberName}`);
        loadCompanyDetails(selectedCompany);
      },
    });
  }, [selectedCompany, loadCompanyDetails]);

  // ════════════════════════════════════════════════════════════════════════════
  // CONFIRMATION DIALOG
  // ════════════════════════════════════════════════════════════════════════════

  const executeConfirmAction = useCallback(async () => {
    if (!confirmAction) return;
    setConfirming(true);
    try {
      await confirmAction.onConfirm();
    } catch (error: any) {
      console.error('Confirm action failed:', error);
      toast.error(error.message || 'Action failed');
    } finally {
      setConfirming(false);
      setConfirmAction(null);
    }
  }, [confirmAction]);

  // ════════════════════════════════════════════════════════════════════════════
  // CSV EXPORT
  // ════════════════════════════════════════════════════════════════════════════

  const handleExportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const csv = await exportFleetCompaniesCSV(companies, memberCounts);
      const date = new Date().toISOString().split('T')[0];
      downloadCsvFile(csv, `fleet-companies-${date}.csv`);
      toast.success('CSV exported');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  }, [companies, memberCounts]);

  // ════════════════════════════════════════════════════════════════════════════
  // FILTERING & SORTING
  // ════════════════════════════════════════════════════════════════════════════

  const filteredCompanies = companies.filter((company) => {
    if (billingFilter !== 'ALL' && company.billingStatus !== billingFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      company.name.toLowerCase().includes(q) ||
      company.ownerEmail.toLowerCase().includes(q) ||
      company.inviteCode.toLowerCase().includes(q) ||
      company.id.toLowerCase().includes(q)
    );
  });

  const sortedCompanies = [...filteredCompanies].sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'name':
        return dir * a.name.localeCompare(b.name);
      case 'ownerEmail':
        return dir * a.ownerEmail.localeCompare(b.ownerEmail);
      case 'billingStatus':
        return dir * a.billingStatus.localeCompare(b.billingStatus);
      case 'memberCount': {
        const countA = memberCounts[a.id] ?? 0;
        const countB = memberCounts[b.id] ?? 0;
        return dir * (countA - countB);
      }
      case 'seatCount':
        return dir * (a.seatCount - b.seatCount);
      case 'createdAt': {
        const timeA = getTimestampSeconds(a.createdAt);
        const timeB = getTimestampSeconds(b.createdAt);
        return dir * (timeA - timeB);
      }
      default:
        return 0;
    }
  });

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  const billingFilterCounts = companies.reduce<Record<string, number>>((acc, c) => {
    acc[c.billingStatus] = (acc[c.billingStatus] ?? 0) + 1;
    return acc;
  }, {});

  // ── Copy Invite Code ──

  const copyInviteCode = useCallback((code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      toast.success('Invite code copied');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  }, []);

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  // ── Detail View ──
  if (selectedCompany) {
    return (
      <div className="space-y-6">
        {/* Back button + company header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => setSelectedCompany(null)}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold">{selectedCompany.name}</h2>
              {billingBadge(selectedCompany.billingStatus)}
              {selectedCompany.homeState && (
                <Badge variant="outline" className="text-xs">
                  <Globe className="h-3 w-3 mr-1" />
                  {selectedCompany.homeState}
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-500">
              {selectedCompany.ownerEmail} · Invite: {selectedCompany.inviteCode}
              <button
                onClick={() => copyInviteCode(selectedCompany.inviteCode)}
                className="ml-2 text-gray-400 hover:text-gray-600"
              >
                <Copy className="h-3 w-3 inline" />
              </button>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => openEditCompany(selectedCompany)}>
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDeleteCompany(selectedCompany)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
            <Button variant="outline" size="sm" onClick={() => loadCompanyDetails(selectedCompany)}>
              <RefreshCw className={`h-4 w-4 ${detailLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="h-5 w-5 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{members.filter(m => m.status === 'active').length}</p>
              <p className="text-xs text-gray-500">Active Members</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Truck className="h-5 w-5 text-amber-500 mx-auto mb-1" />
              <div className="text-2xl font-bold">
                {members.filter(m => m.status === 'active').length} / {selectedCompany.seatCount}
              </div>
              <p className="text-xs text-gray-500">Seat Usage</p>
              <Progress
                value={(members.filter(m => m.status === 'active').length / Math.max(selectedCompany.seatCount, 1)) * 100}
                className="mt-2 h-1.5"
              />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Briefcase className="h-5 w-5 text-indigo-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{jobs.filter(j => ['pending', 'accepted', 'in_progress'].includes(j.status)).length}</p>
              <p className="text-xs text-gray-500">Active Jobs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{alerts.filter(a => !a.dismissed).length}</p>
              <p className="text-xs text-gray-500">Open Alerts</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <MessageSquare className="h-5 w-5 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{messages.length}</p>
              <p className="text-xs text-gray-500">Messages</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CreditCard className="h-5 w-5 text-purple-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">
                {selectedCompany.pricePerSeat > 0
                  ? formatCurrency(selectedCompany.seatCount * selectedCompany.pricePerSeat)
                  : '—'}
              </p>
              <p className="text-xs text-gray-500">Monthly Bill</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b pb-2">
          {(['members', 'jobs', 'alerts', 'messages', 'billing', 'settings'] as DetailTab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setDetailTab(tab)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                detailTab === tab
                  ? 'bg-white border border-b-white text-amber-600 -mb-[1px]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {detailLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {detailTab === 'members' && renderMembersTab()}
            {detailTab === 'jobs' && renderJobsTab()}
            {detailTab === 'alerts' && renderAlertsTab()}
            {detailTab === 'messages' && renderMessagesTab()}
            {detailTab === 'billing' && renderBillingTab()}
            {detailTab === 'settings' && renderSettingsTab()}
          </>
        )}

        {/* Modals for detail view context */}
        {editingCompany && renderEditModal()}
        {confirmAction && renderConfirmDialog()}
      </div>
    );
  }

  // ── Empty State (no companies at all) ──
  if (companies.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Card className="max-w-md w-full">
            <CardContent className="py-12 text-center">
              <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <Building2 className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No fleet companies yet</h3>
              <p className="text-gray-500 text-sm mb-6">
                Fleet companies let you group NavigatorX users under a shared account
                with dispatching, GPS tracking, and fleet alerts.
              </p>
              <Button
                onClick={openCreateModal}
                variant="amber"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Company
              </Button>
            </CardContent>
          </Card>
        </div>

        {showCreateModal && renderCreateModal()}
        {confirmAction && renderConfirmDialog()}
      </div>
    );
  }

  // ── List View ──
  return (
    <div className="space-y-6">
      {/* Stats overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Companies</p>
                  <p className="text-2xl font-bold">{stats.totalCompanies}</p>
                </div>
                <Building2 className="h-8 w-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Active</p>
                  <p className="text-2xl font-bold text-green-600">{stats.activeCompanies}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Trialing</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.trialingCompanies}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Members</p>
                  <p className="text-2xl font-bold">{stats.totalMembers}</p>
                </div>
                <Users className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Seats</p>
                  <p className="text-2xl font-bold">{stats.totalSeats}</p>
                </div>
                <Truck className="h-8 w-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Utilization</p>
                  <p className="text-2xl font-bold">{stats.seatUtilizationPercent}%</p>
                </div>
                <BarChart3 className="h-8 w-8 text-indigo-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Est. MRR</p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.estimatedMrr > 0 ? formatCurrency(stats.estimatedMrr) : '—'}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Toolbar: Create, Search, Filter, Export, Refresh */}
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={openCreateModal} variant="amber">
          <Plus className="h-4 w-4 mr-2" />
          Create Company
        </Button>

        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search companies, emails, invite codes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <select
          value={billingFilter}
          onChange={(e) => setBillingFilter(e.target.value as BillingFilter)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm bg-white"
        >
          {BILLING_FILTER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
              {opt.value !== 'ALL' && billingFilterCounts[opt.value]
                ? ` (${billingFilterCounts[opt.value]})`
                : opt.value === 'ALL' ? ` (${companies.length})` : ''}
            </option>
          ))}
        </select>

        <Button
          variant="outline"
          onClick={handleExportCsv}
          disabled={exporting || companies.length === 0}
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export CSV
        </Button>

        <Button variant="outline" onClick={loadCompanies}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Companies table */}
      {sortedCompanies.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No companies match your filters</p>
            <p className="text-sm text-gray-400 mt-1">
              Try adjusting your search or filter criteria
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <SortHeader field="name" label="Company" activeSortField={sortField} activeSortDirection={sortDirection} onSort={handleSort} />
                  <SortHeader field="ownerEmail" label="Owner" activeSortField={sortField} activeSortDirection={sortDirection} onSort={handleSort} />
                  <SortHeader field="billingStatus" label="Status" align="center" activeSortField={sortField} activeSortDirection={sortDirection} onSort={handleSort} />
                  <SortHeader field="memberCount" label="Members" align="center" activeSortField={sortField} activeSortDirection={sortDirection} onSort={handleSort} />
                  <SortHeader field="seatCount" label="Seats" align="center" activeSortField={sortField} activeSortDirection={sortDirection} onSort={handleSort} />
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Invite Code</th>
                  <SortHeader field="createdAt" label="Created" activeSortField={sortField} activeSortDirection={sortDirection} onSort={handleSort} />
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedCompanies.map((company) => (
                  <tr
                    key={company.id}
                    className="border-b hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleSelectCompany(company)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-amber-500 flex-shrink-0" />
                        <div>
                          <span className="font-medium">{company.name}</span>
                          {company.homeState && (
                            <span className="text-xs text-gray-400 ml-2">{company.homeState}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{company.ownerEmail}</td>
                    <td className="px-4 py-3 text-center">{billingBadge(company.billingStatus)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-medium">{memberCounts[company.id] ?? '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-center">{company.seatCount}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono">
                          {company.inviteCode}
                        </code>
                        <button
                          onClick={(e) => { e.stopPropagation(); copyInviteCode(company.inviteCode); }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{formatShortDate(company.createdAt)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleSelectCompany(company); }}
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); openEditCompany(company); }}
                          title="Edit company"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDeleteCompany(company); }}
                          title="Delete company"
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* List-view modals */}
      {showCreateModal && renderCreateModal()}
      {editingCompany && renderEditModal()}
      {confirmAction && renderConfirmDialog()}
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // TAB RENDERERS
  // ════════════════════════════════════════════════════════════════════════════

  function renderMembersTab() {
    const activeMembers = members.filter(m => m.status !== 'removed');
    const removedMembers = members.filter(m => m.status === 'removed');

    return (
      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Active Members ({activeMembers.length})</CardTitle>
          </CardHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Name</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Email</th>
                  <th className="text-center px-4 py-2 font-medium text-gray-600">Role</th>
                  <th className="text-center px-4 py-2 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Last Location</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Last Seen</th>
                  <th className="text-center px-4 py-2 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeMembers.map((member) => (
                  <tr key={member.uid} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{member.displayName || '—'}</td>
                    <td className="px-4 py-2 text-gray-600">{member.email}</td>
                    <td className="px-4 py-2 text-center">{roleBadge(member.role)}</td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {statusBadge(member.status)}
                        {member.isMoving && (
                          <span className="inline-flex items-center gap-1 text-xs text-green-600">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                            Moving
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">
                      {member.lastLocationLat && member.lastLocationLng ? (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {member.lastLocationLat.toFixed(4)}, {member.lastLocationLng.toFixed(4)}
                          {member.lastLocationSpeed !== undefined && (
                            <span className="ml-1 text-blue-600">{Math.round(member.lastLocationSpeed)} mph</span>
                          )}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{formatTimeAgo(member.lastLocationAt)}</td>
                    <td className="px-4 py-2 text-center">
                      {member.role !== 'owner' && (
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUpdateMemberRole(member, member.role === 'admin' ? 'driver' : 'admin')}
                            title={member.role === 'admin' ? 'Demote to Driver' : 'Promote to Admin'}
                          >
                            <Shield className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSuspendMember(member)}
                            title={member.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                          >
                            {member.status === 'suspended' ? (
                              <UserCheck className="h-3.5 w-3.5 text-green-600" />
                            ) : (
                              <UserX className="h-3.5 w-3.5 text-yellow-600" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMember(member)}
                            title="Remove from company"
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {activeMembers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                      No active members
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {removedMembers.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400">Removed Members ({removedMembers.length})</CardTitle>
            </CardHeader>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {removedMembers.map((member) => (
                    <tr key={member.uid} className="border-b text-gray-400">
                      <td className="px-4 py-2">{member.displayName || '—'}</td>
                      <td className="px-4 py-2">{member.email}</td>
                      <td className="px-4 py-2 text-center">{roleBadge(member.role)}</td>
                      <td className="px-4 py-2 text-center">{statusBadge(member.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    );
  }

  function renderJobsTab() {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Jobs ({jobs.length})</CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left px-4 py-2 font-medium text-gray-600">Title</th>
                <th className="text-center px-4 py-2 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Destination</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Assigned To</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Created</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const assignedMember = members.find(m => m.uid === job.assignedTo);
                return (
                  <tr key={job.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <div>
                        <span className="font-medium">{job.title}</span>
                        {job.description && (
                          <p className="text-xs text-gray-400 truncate max-w-xs">{job.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-center">{jobStatusBadge(job.status)}</td>
                    <td className="px-4 py-2 text-gray-600 text-xs">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {job.destinationName || `${job.destinationLat.toFixed(4)}, ${job.destinationLng.toFixed(4)}`}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {assignedMember?.displayName || assignedMember?.email || job.assignedTo}
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{formatTimestamp(job.createdAt)}</td>
                  </tr>
                );
              })}
              {jobs.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                    No jobs dispatched
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    );
  }

  function renderAlertsTab() {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Fleet Alerts ({alerts.length})</CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-center px-4 py-2 font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Driver</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Details</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Location</th>
                <th className="text-center px-4 py-2 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Time</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((alert) => (
                <tr key={alert.id} className={`border-b hover:bg-gray-50 ${alert.dismissed ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-2 text-center">
                    {alert.type === 'idle' ? (
                      <Badge className="bg-yellow-100 text-yellow-700">
                        <Timer className="h-3 w-3 mr-1" />
                        Idle
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700">
                        <Gauge className="h-3 w-3 mr-1" />
                        Speeding
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 font-medium">{alert.driverName}</td>
                  <td className="px-4 py-2 text-gray-600 text-xs">
                    {alert.type === 'idle'
                      ? `Idle for ${alert.minutesIdle ?? '?'} minutes`
                      : `${alert.speedMph ?? '?'} mph (limit: ${alert.speedLimitMph ?? '?'} mph)`}
                  </td>
                  <td className="px-4 py-2 text-gray-500 text-xs">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {alert.lat.toFixed(4)}, {alert.lng.toFixed(4)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {alert.dismissed ? (
                      <Badge variant="outline" className="text-gray-400">Dismissed</Badge>
                    ) : (
                      <Badge className="bg-orange-100 text-orange-700">Active</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-500 text-xs">{formatTimeAgo(alert.createdAt)}</td>
                </tr>
              ))}
              {alerts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                    No fleet alerts
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    );
  }

  function renderMessagesTab() {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Messages ({messages.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {messages.map((msg) => (
              <div key={msg.id} className="flex gap-3 p-3 rounded-lg bg-gray-50">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700">
                    {(msg.fromName || '?').charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-medium text-sm">{msg.fromName || msg.fromUid}</span>
                    {msg.toUid === null ? (
                      <Badge variant="outline" className="text-xs">Broadcast</Badge>
                    ) : (
                      <span className="text-xs text-gray-400">→ {msg.toUid}</span>
                    )}
                    {msg.type !== 'text' && (
                      <Badge variant="outline" className="text-xs">{msg.type}</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-700">{msg.text}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatTimestamp(msg.createdAt)}</p>
                </div>
              </div>
            ))}
            {messages.length === 0 && (
              <div className="py-8 text-center text-gray-400">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                No messages
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // BILLING TAB
  // ════════════════════════════════════════════════════════════════════════════

  function renderBillingTab() {
    if (!selectedCompany) return null;
    const c = selectedCompany;
    const monthlyTotal = c.seatCount * c.pricePerSeat;

    const statusBadgeColor = (status: string) => {
      switch (status.toLowerCase()) {
        case 'active': return 'bg-emerald-100 text-emerald-700';
        case 'trialing': return 'bg-blue-100 text-blue-700';
        case 'past_due': return 'bg-red-100 text-red-700';
        case 'cancelled': case 'canceled': return 'bg-gray-100 text-gray-600';
        case 'cancelling': return 'bg-yellow-100 text-yellow-700';
        default: return 'bg-gray-100 text-gray-600';
      }
    };

    const invoiceStatusColor = (status: string) => {
      switch (status) {
        case 'paid': return 'bg-emerald-100 text-emerald-700';
        case 'sent': return 'bg-blue-100 text-blue-700';
        case 'overdue': return 'bg-red-100 text-red-700';
        case 'draft': return 'bg-gray-100 text-gray-600';
        case 'void': case 'cancelled': return 'bg-gray-100 text-gray-500';
        default: return 'bg-gray-100 text-gray-600';
      }
    };

    async function handleCreateSubscription() {
      setBillingActionLoading(true);
      try {
        const result = await callCreateFleetSubscription(c.id);
        toast.success(`Subscription created: ${result.subscriptionId}`);
        loadCompanyDetails(c);
        loadCompanies();
      } catch (error: any) {
        toast.error(error?.message || 'Failed to create subscription');
      } finally {
        setBillingActionLoading(false);
      }
    }

    async function handleCancelSubscription(immediate: boolean) {
      setBillingActionLoading(true);
      try {
        await callCancelFleetSubscription(c.id, immediate);
        toast.success(immediate ? 'Subscription cancelled immediately' : 'Subscription set to cancel at period end');
        loadCompanyDetails(c);
        loadCompanies();
      } catch (error: any) {
        toast.error(error?.message || 'Failed to cancel subscription');
      } finally {
        setBillingActionLoading(false);
      }
    }

    async function handleGenerateQuote(sendEmailFlag: boolean) {
      setBillingActionLoading(true);
      try {
        const result = await callGenerateFleetQuote({
          companyId: c.id,
          seatCount: quoteForm.seatCount || c.seatCount,
          pricePerSeat: quoteForm.pricePerSeat || undefined,
          contactName: quoteForm.contactName,
          contactEmail: quoteForm.contactEmail,
          notes: quoteForm.notes || undefined,
          sendEmail: sendEmailFlag,
        });
        toast.success(`Quote ${result.quoteNumber} generated${sendEmailFlag ? ' & sent' : ''}`);
        setShowQuoteModal(false);
        loadCompanyDetails(c);
      } catch (error: any) {
        toast.error(error?.message || 'Failed to generate quote');
      } finally {
        setBillingActionLoading(false);
      }
    }

    async function handleGenerateInvoice(sendEmailFlag: boolean) {
      setBillingActionLoading(true);
      try {
        const result = await callGenerateFleetInvoice({
          companyId: c.id,
          periodStart: invoiceForm.periodStart || undefined,
          periodEnd: invoiceForm.periodEnd || undefined,
          notes: invoiceForm.notes || undefined,
          sendEmail: sendEmailFlag,
        });
        toast.success(`Invoice ${result.invoiceNumber} generated${sendEmailFlag ? ' & sent' : ''}`);
        setShowInvoiceModal(false);
        loadCompanyDetails(c);
      } catch (error: any) {
        toast.error(error?.message || 'Failed to generate invoice');
      } finally {
        setBillingActionLoading(false);
      }
    }

    async function handleSetPricing() {
      setBillingActionLoading(true);
      try {
        await callSetCustomPricing({
          companyId: c.id,
          pricePerSeat: pricingForm.pricePerSeat || undefined,
          customDiscount: pricingForm.customDiscount || undefined,
          tierName: pricingForm.tierName || undefined,
        });
        toast.success('Custom pricing saved');
        setShowPricingModal(false);
        loadCompanyDetails(c);
        loadCompanies();
      } catch (error: any) {
        toast.error(error?.message || 'Failed to set pricing');
      } finally {
        setBillingActionLoading(false);
      }
    }

    async function handleSendInvoice(invoiceId: string) {
      try {
        await callSendFleetInvoice(c.id, invoiceId);
        toast.success('Invoice sent');
        loadCompanyDetails(c);
      } catch (error: any) {
        toast.error(error?.message || 'Failed to send invoice');
      }
    }

    async function handleOpenPortal() {
      try {
        const url = await callGetBillingPortalUrl(c.id);
        window.open(url, '_blank');
      } catch (error: any) {
        toast.error(error?.message || 'Failed to open billing portal');
      }
    }

    return (
      <div className="space-y-6">
        {/* Subscription Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5" /> Subscription</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-gray-500 uppercase font-medium">Status</div>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadgeColor(c.billingStatus)}`}>
                  {c.billingStatus || 'None'}
                </span>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase font-medium">Tier</div>
                <div className="text-sm font-medium mt-1">{c.tierName || 'Base'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase font-medium">Price / Seat</div>
                <div className="text-sm font-medium mt-1">{c.pricePerSeat ? `$${(c.pricePerSeat / 100).toFixed(2)}/mo` : '—'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase font-medium">Monthly Total</div>
                <div className="text-sm font-bold text-blue-700 mt-1">{monthlyTotal > 0 ? `$${(monthlyTotal / 100).toFixed(2)}` : '—'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase font-medium">Seats</div>
                <div className="text-sm font-medium mt-1">{c.seatCount}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase font-medium">Period End</div>
                <div className="text-sm mt-1">{c.currentPeriodEnd ? new Date((c.currentPeriodEnd as any).seconds * 1000).toLocaleDateString() : '—'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase font-medium">Stripe Customer</div>
                <div className="text-xs font-mono mt-1 text-gray-600 truncate">{c.stripeCustomerId || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase font-medium">Stripe Subscription</div>
                <div className="text-xs font-mono mt-1 text-gray-600 truncate">{c.stripeSubscriptionId || '—'}</div>
              </div>
            </div>

            <div className="flex gap-2 mt-4 pt-4 border-t">
              {!c.stripeSubscriptionId ? (
                <Button size="sm" onClick={handleCreateSubscription} disabled={billingActionLoading}>
                  <Plus className="h-4 w-4 mr-1" /> Create Subscription
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={handleOpenPortal}>
                    <ExternalLink className="h-4 w-4 mr-1" /> Stripe Portal
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => handleCancelSubscription(false)} disabled={billingActionLoading}>
                    Cancel at Period End
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => {
              setQuoteForm({ contactName: c.billingContactName || '', contactEmail: c.billingEmail || c.ownerEmail, seatCount: c.seatCount, pricePerSeat: c.pricePerSeat, notes: '' });
              setShowQuoteModal(true);
            }}>
              <FileText className="h-4 w-4 mr-1" /> Generate Quote
            </Button>
            <Button size="sm" variant="outline" onClick={() => {
              setInvoiceForm({ periodStart: '', periodEnd: '', notes: '' });
              setShowInvoiceModal(true);
            }}>
              <Receipt className="h-4 w-4 mr-1" /> Generate Invoice
            </Button>
            <Button size="sm" variant="outline" onClick={() => {
              setPricingForm({ pricePerSeat: c.pricePerSeat, customDiscount: c.customDiscount, tierName: c.tierName });
              setShowPricingModal(true);
            }}>
              <DollarSign className="h-4 w-4 mr-1" /> Set Custom Pricing
            </Button>
          </CardContent>
        </Card>

        {/* Invoices Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Invoices ({invoices.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No invoices yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-2 font-medium">Invoice</th>
                      <th className="pb-2 font-medium">Period</th>
                      <th className="pb-2 font-medium">Seats</th>
                      <th className="pb-2 font-medium text-right">Amount</th>
                      <th className="pb-2 font-medium">Status</th>
                      <th className="pb-2 font-medium">Sent</th>
                      <th className="pb-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id} className="border-b last:border-0">
                        <td className="py-2 font-mono text-xs">{inv.invoiceNumber}</td>
                        <td className="py-2 text-xs">{inv.periodStart} — {inv.periodEnd}</td>
                        <td className="py-2">{inv.seatCount}</td>
                        <td className="py-2 text-right font-medium">${(inv.total / 100).toFixed(2)}</td>
                        <td className="py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${invoiceStatusColor(inv.status)}`}>{inv.status}</span>
                        </td>
                        <td className="py-2 text-xs text-gray-500">{inv.sentAt ? new Date((inv.sentAt as any).seconds * 1000).toLocaleDateString() : '—'}</td>
                        <td className="py-2">
                          <div className="flex gap-1">
                            {inv.status !== 'paid' && (
                              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleSendInvoice(inv.id)}>
                                <Send className="h-3 w-3" />
                              </Button>
                            )}
                            {inv.pdfDownloadUrl && (
                              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => window.open(inv.pdfDownloadUrl, '_blank')}>
                                <Download className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quotes Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quotes ({quotes.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {quotes.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No quotes yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-2 font-medium">Quote</th>
                      <th className="pb-2 font-medium">Contact</th>
                      <th className="pb-2 font-medium">Seats</th>
                      <th className="pb-2 font-medium text-right">Monthly</th>
                      <th className="pb-2 font-medium">Status</th>
                      <th className="pb-2 font-medium">Valid Until</th>
                      <th className="pb-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map(q => (
                      <tr key={q.id} className="border-b last:border-0">
                        <td className="py-2 font-mono text-xs">{q.quoteNumber}</td>
                        <td className="py-2 text-xs">{q.contactName}</td>
                        <td className="py-2">{q.seatCount}</td>
                        <td className="py-2 text-right font-medium">${(q.monthlyTotal / 100).toFixed(2)}</td>
                        <td className="py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${invoiceStatusColor(q.status)}`}>{q.status}</span>
                        </td>
                        <td className="py-2 text-xs text-gray-500">{q.validUntil ? new Date((q.validUntil as any).seconds * 1000).toLocaleDateString() : '—'}</td>
                        <td className="py-2">
                          {q.pdfDownloadUrl && (
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => window.open(q.pdfDownloadUrl, '_blank')}>
                              <Download className="h-3 w-3" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Generate Quote Modal */}
        <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generate Quote for {c.name}</DialogTitle>
              <DialogDescription>Create a pricing quote with PDF attachment.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input value={quoteForm.contactName} onChange={e => setQuoteForm(f => ({ ...f, contactName: e.target.value }))} />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input type="email" value={quoteForm.contactEmail} onChange={e => setQuoteForm(f => ({ ...f, contactEmail: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Seat Count</Label>
                  <Input type="number" value={quoteForm.seatCount} onChange={e => setQuoteForm(f => ({ ...f, seatCount: parseInt(e.target.value) || 0 }))} />
                </div>
                <div>
                  <Label>Price Per Seat (cents)</Label>
                  <Input type="number" value={quoteForm.pricePerSeat} onChange={e => setQuoteForm(f => ({ ...f, pricePerSeat: parseInt(e.target.value) || 0 }))} placeholder="0 = auto from tier" />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={quoteForm.notes} onChange={e => setQuoteForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowQuoteModal(false)}>Cancel</Button>
              <Button variant="outline" onClick={() => handleGenerateQuote(false)} disabled={billingActionLoading || !quoteForm.contactName || !quoteForm.contactEmail}>
                {billingActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />} Draft
              </Button>
              <Button onClick={() => handleGenerateQuote(true)} disabled={billingActionLoading || !quoteForm.contactName || !quoteForm.contactEmail}>
                {billingActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Generate & Send
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Generate Invoice Modal */}
        <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generate Invoice for {c.name}</DialogTitle>
              <DialogDescription>{c.seatCount} seats x ${c.pricePerSeat ? (c.pricePerSeat / 100).toFixed(2) : '0.00'}/seat = ${monthlyTotal > 0 ? (monthlyTotal / 100).toFixed(2) : '0.00'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Period Start</Label>
                  <Input type="date" value={invoiceForm.periodStart} onChange={e => setInvoiceForm(f => ({ ...f, periodStart: e.target.value }))} />
                </div>
                <div>
                  <Label>Period End</Label>
                  <Input type="date" value={invoiceForm.periodEnd} onChange={e => setInvoiceForm(f => ({ ...f, periodEnd: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={invoiceForm.notes} onChange={e => setInvoiceForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>Cancel</Button>
              <Button variant="outline" onClick={() => handleGenerateInvoice(false)} disabled={billingActionLoading}>
                {billingActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />} Draft
              </Button>
              <Button onClick={() => handleGenerateInvoice(true)} disabled={billingActionLoading}>
                {billingActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Generate & Send
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Set Custom Pricing Modal */}
        <Dialog open={showPricingModal} onOpenChange={setShowPricingModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Custom Pricing for {c.name}</DialogTitle>
              <DialogDescription>Override automatic tier-based pricing for this company.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Price Per Seat (cents)</Label>
                <Input type="number" value={pricingForm.pricePerSeat} onChange={e => setPricingForm(f => ({ ...f, pricePerSeat: parseInt(e.target.value) || 0 }))} />
                <p className="text-xs text-gray-500 mt-1">= ${pricingForm.pricePerSeat ? (pricingForm.pricePerSeat / 100).toFixed(2) : '0.00'}/seat/month</p>
              </div>
              <div>
                <Label>Custom Discount (%)</Label>
                <Input type="number" value={pricingForm.customDiscount} onChange={e => setPricingForm(f => ({ ...f, customDiscount: parseInt(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label>Tier Name</Label>
                <Input value={pricingForm.tierName} onChange={e => setPricingForm(f => ({ ...f, tierName: e.target.value }))} placeholder="Starter / Professional / Enterprise / Custom" />
              </div>
              <div className="bg-gray-50 rounded p-3 text-xs text-gray-600 space-y-1">
                <div>Default tiers: Starter (5-25 seats, 15% off), Professional (26-100, 25% off), Enterprise (101+, custom)</div>
                <div>Base price: $9.00/seat/month (900 cents)</div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPricingModal(false)}>Cancel</Button>
              <Button onClick={handleSetPricing} disabled={billingActionLoading}>
                {billingActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save Pricing
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  function renderSettingsTab() {
    if (!selectedCompany) return null;
    return (
      <div className="grid md:grid-cols-2 gap-4">
        {/* Company Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Company Settings</CardTitle>
            <CardDescription className="text-xs">Current configuration for this fleet company</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm text-gray-600">Company Name</span>
              <span className="font-medium">{selectedCompany.name}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm text-gray-600">Owner UID</span>
              <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{selectedCompany.ownerUid}</code>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm text-gray-600">Owner Email</span>
              <span className="text-sm">{selectedCompany.ownerEmail}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm text-gray-600">Billing Status</span>
              {billingBadge(selectedCompany.billingStatus)}
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm text-gray-600">Seat Count</span>
              <span className="font-medium">{selectedCompany.seatCount}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm text-gray-600">Invite Code</span>
              <div className="flex items-center gap-1">
                <code className="text-sm bg-gray-100 px-2 py-0.5 rounded font-mono">{selectedCompany.inviteCode}</code>
                <button onClick={() => copyInviteCode(selectedCompany.inviteCode)} className="text-gray-400 hover:text-gray-600">
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm text-gray-600">Home State</span>
              <span className="font-medium">{selectedCompany.homeState || '—'}</span>
            </div>
            <div className="flex items-start justify-between py-2 border-b">
              <span className="text-sm text-gray-600">Active States</span>
              <div className="flex flex-wrap gap-1 justify-end max-w-[200px]">
                {selectedCompany.activeStates.length > 0 ? (
                  selectedCompany.activeStates.map(s => (
                    <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                  ))
                ) : (
                  <span className="text-sm text-gray-400">—</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alert Thresholds + Stripe Info */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Alert Thresholds</CardTitle>
              <CardDescription className="text-xs">Fleet monitoring configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm text-gray-600">
                  <Timer className="h-4 w-4 inline mr-1" />
                  Idle Alert Threshold
                </span>
                <span className="font-medium">{selectedCompany.idleAlertMinutes} minutes</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm text-gray-600">
                  <Gauge className="h-4 w-4 inline mr-1" />
                  Speed Limit
                </span>
                <span className="font-medium">{selectedCompany.speedLimitMph} mph</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Created</span>
                <span className="text-sm text-gray-500">{formatTimestamp(selectedCompany.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Last Updated</span>
                <span className="text-sm text-gray-500">{formatTimestamp(selectedCompany.updatedAt)}</span>
              </div>
              <Button variant="outline" className="w-full mt-4" onClick={() => openEditCompany(selectedCompany)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Settings
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Stripe Billing</CardTitle>
              <CardDescription className="text-xs">Read-only Stripe integration details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Customer ID</span>
                <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                  {selectedCompany.stripeCustomerId || '—'}
                </code>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Subscription ID</span>
                <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                  {selectedCompany.stripeSubscriptionId || '—'}
                </code>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Price Per Seat</span>
                <span className="font-medium">
                  {selectedCompany.pricePerSeat > 0 ? formatCurrency(selectedCompany.pricePerSeat) : '—'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Period End</span>
                <span className="text-sm text-gray-500">
                  {selectedCompany.currentPeriodEnd
                    ? formatShortDate(selectedCompany.currentPeriodEnd)
                    : '—'}
                </span>
              </div>
            </CardContent>
          </Card>

          <FleetVoicePolicyPanel
            companyId={selectedCompany.id}
            companyName={selectedCompany.name}
            initialPolicyEnabled={Boolean((selectedCompany as any).voicePolicyEnabled)}
          />
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // MODALS
  // ════════════════════════════════════════════════════════════════════════════

  function renderCreateModal() {
    return (
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Fleet Company</DialogTitle>
            <DialogDescription>
              Set up a new company account. The owner must already have a NavigatorX account.
            </DialogDescription>
          </DialogHeader>

          {createdInviteCode ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="font-semibold text-green-800">Company Created</p>
                <p className="text-sm text-green-600 mt-1">Share this invite code with members:</p>
                <div className="mt-3 flex items-center justify-center gap-2">
                  <code className="text-2xl font-mono font-bold bg-white px-4 py-2 rounded border tracking-widest">
                    {createdInviteCode}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyInviteCode(createdInviteCode)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setShowCreateModal(false)} variant="amber">
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="create-name">Company Name *</Label>
                <Input
                  id="create-name"
                  value={createForm.name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Acme Oilfield Services"
                />
              </div>

              <div>
                <Label htmlFor="create-email">Owner Email *</Label>
                <Input
                  id="create-email"
                  type="email"
                  value={createForm.ownerEmail}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, ownerEmail: e.target.value }))}
                  placeholder="owner@example.com"
                />
                <p className="text-xs text-gray-400 mt-1">Must match an existing NavigatorX user</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="create-seats">Seat Count</Label>
                  <Input
                    id="create-seats"
                    type="number"
                    min={1}
                    value={createForm.seatCount}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, seatCount: parseInt(e.target.value) || 1 }))}
                  />
                </div>
                <div>
                  <Label htmlFor="create-billing">Billing Status</Label>
                  <select
                    id="create-billing"
                    value={createForm.billingStatus}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, billingStatus: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="TRIALING">Trialing</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="create-home-state">Home State</Label>
                <select
                  id="create-home-state"
                  value={createForm.homeState}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, homeState: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Select a state...</option>
                  {US_STATES.map(s => (
                    <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Active States</Label>
                <p className="text-xs text-gray-400 mb-2">Select states where this company operates</p>
                <div className="max-h-32 overflow-y-auto border rounded-md p-2 grid grid-cols-3 gap-1">
                  {US_STATES.map(s => (
                    <label
                      key={s.code}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer hover:bg-gray-50 ${
                        createForm.activeStates.includes(s.code) ? 'bg-amber-50 text-amber-700 font-medium' : 'text-gray-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={createForm.activeStates.includes(s.code)}
                        onChange={() => toggleActiveState(s.code, 'create')}
                        className="rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                      />
                      {s.code}
                    </label>
                  ))}
                </div>
                {createForm.activeStates.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{createForm.activeStates.length} selected</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="create-idle">Idle Alert (min)</Label>
                  <Input
                    id="create-idle"
                    type="number"
                    min={5}
                    value={createForm.idleAlertMinutes}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, idleAlertMinutes: parseInt(e.target.value) || 30 }))}
                  />
                </div>
                <div>
                  <Label htmlFor="create-speed">Speed Limit (mph)</Label>
                  <Input
                    id="create-speed"
                    type="number"
                    min={25}
                    value={createForm.speedLimitMph}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, speedLimitMph: parseInt(e.target.value) || 75 }))}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateModal(false)} disabled={creating}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateCompany}
                  disabled={creating || !createForm.name.trim() || !createForm.ownerEmail.trim()}
                  variant="amber"
                >
                  {creating ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Create Company
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  function renderEditModal() {
    if (!editingCompany) return null;

    return (
      <Dialog open={!!editingCompany} onOpenChange={(open) => { if (!open) setEditingCompany(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
            <DialogDescription>
              Update settings for {editingCompany.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Company Name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-seats">Seat Count</Label>
                <Input
                  id="edit-seats"
                  type="number"
                  min={1}
                  value={editForm.seatCount}
                  onChange={(e) => setEditForm(prev => ({ ...prev, seatCount: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div>
                <Label htmlFor="edit-billing">Billing Status</Label>
                <select
                  id="edit-billing"
                  value={editForm.billingStatus}
                  onChange={(e) => setEditForm(prev => ({ ...prev, billingStatus: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="TRIALING">Trialing</option>
                  <option value="PAST_DUE">Past Due</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="edit-home-state">Home State</Label>
              <select
                id="edit-home-state"
                value={editForm.homeState}
                onChange={(e) => setEditForm(prev => ({ ...prev, homeState: e.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Select a state...</option>
                {US_STATES.map(s => (
                  <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Active States</Label>
              <div className="max-h-32 overflow-y-auto border rounded-md p-2 grid grid-cols-3 gap-1">
                {US_STATES.map(s => (
                  <label
                    key={s.code}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer hover:bg-gray-50 ${
                      editForm.activeStates.includes(s.code) ? 'bg-amber-50 text-amber-700 font-medium' : 'text-gray-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={editForm.activeStates.includes(s.code)}
                      onChange={() => toggleActiveState(s.code, 'edit')}
                      className="rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                    />
                    {s.code}
                  </label>
                ))}
              </div>
              {editForm.activeStates.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{editForm.activeStates.length} selected</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-idle">Idle Alert (min)</Label>
                <Input
                  id="edit-idle"
                  type="number"
                  min={5}
                  value={editForm.idleAlertMinutes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, idleAlertMinutes: parseInt(e.target.value) || 30 }))}
                />
              </div>
              <div>
                <Label htmlFor="edit-speed">Speed Limit (mph)</Label>
                <Input
                  id="edit-speed"
                  type="number"
                  min={25}
                  value={editForm.speedLimitMph}
                  onChange={(e) => setEditForm(prev => ({ ...prev, speedLimitMph: parseInt(e.target.value) || 75 }))}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCompany(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveCompany} disabled={saving} variant="amber">
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  function renderConfirmDialog() {
    if (!confirmAction) return null;
    const isDanger = confirmAction.variant === 'danger';

    return (
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className={isDanger ? 'text-red-600' : 'text-yellow-600'}>
              {confirmAction.title}
            </DialogTitle>
            <DialogDescription>
              {confirmAction.description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)} disabled={confirming}>
              Cancel
            </Button>
            <Button
              onClick={executeConfirmAction}
              disabled={confirming}
              className={isDanger
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-yellow-600 hover:bg-yellow-700 text-white'
              }
            >
              {confirming && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {confirmAction.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
}
